import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientAssignmentService } from '../../services/clientAssignmentService';
import { clientService } from '../../services/clientService';
import { User } from '../../services/userService';

interface UserClientAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

const UserClientAssignmentDialog: React.FC<UserClientAssignmentDialogProps> = ({
  open,
  onClose,
  user,
}) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const queryClient = useQueryClient();

  // Fetch user's assigned clients
  const { data: assignedClientsData, isLoading: assignedLoading } = useQuery({
    queryKey: ['assigned-clients', user?.id],
    queryFn: () => user ? clientAssignmentService.getAssignedClients(user.id) : Promise.resolve({ success: false, data: [] }),
    enabled: open && !!user,
  });

  // Fetch all clients for assignment dropdown
  const { data: allClientsData } = useQuery({
    queryKey: ['clients-for-assignment'],
    queryFn: () => clientService.getClients({ page: 0, limit: 100 }),
    enabled: open,
  });

  // Create assignment mutation
  const createAssignmentMutation = useMutation({
    mutationFn: clientAssignmentService.createAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assigned-clients', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['client-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setSelectedClientId('');
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: clientAssignmentService.removeAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assigned-clients', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['client-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const assignedClients = assignedClientsData?.data || [];
  const allClients = allClientsData?.data || [];

  // Get unassigned clients
  const assignedClientIds = assignedClients.map(client => client.id);
  const unassignedClients = allClients.filter(client => !assignedClientIds.includes(client.id));

  const handleAssignClient = () => {
    if (selectedClientId && user) {
      createAssignmentMutation.mutate({
        clientId: selectedClientId,
        adminId: user.id,
      });
    }
  };

  const handleRemoveAssignment = (clientId: string) => {
    // Find the assignment to remove
    const assignment = assignedClients.find(client => client.id === clientId);
    if (assignment && (assignment as any).assignmentId) {
      // Use the actual assignment ID from the backend
      removeAssignmentMutation.mutate((assignment as any).assignmentId);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Client Assignments for {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email} • {user.role}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Assign New Client Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Assign New Client
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl fullWidth>
                <InputLabel>Select Client to Assign</InputLabel>
                <Select
                  value={selectedClientId}
                  label="Select Client to Assign"
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={unassignedClients.length === 0}
                >
                  {unassignedClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {client.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {client.email} • {client.company || 'No company'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAssignClient}
                disabled={!selectedClientId || createAssignmentMutation.isPending}
              >
                {createAssignmentMutation.isPending ? 'Assigning...' : 'Assign'}
              </Button>
            </Box>
            
            {unassignedClients.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                All clients are already assigned to this admin user.
              </Alert>
            )}

            {createAssignmentMutation.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(createAssignmentMutation.error as any)?.response?.data?.error || 'Failed to assign client'}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Current Assignments Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Current Client Assignments ({assignedClients.length})
            </Typography>
            
            {assignedLoading ? (
              <Typography>Loading assignments...</Typography>
            ) : assignedClients.length === 0 ? (
              <Alert severity="info">
                This admin user has no client assignments yet.
              </Alert>
            ) : (
              <List>
                {assignedClients.map((client) => (
                  <ListItem key={client.id} divider>
                    <BusinessIcon color="action" sx={{ mr: 2 }} />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {client.name}
                          </Typography>
                          <Chip 
                            label={client.country || 'No country'} 
                            size="small" 
                            variant="outlined" 
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {client.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Company: {client.company || 'Not specified'} • 
                            Assigned by: {client.assignedBy ? 
                              `${client.assignedBy.firstName} ${client.assignedBy.lastName}` : 
                              'Unknown'
                            }
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveAssignment(client.id)}
                        disabled={removeAssignmentMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            {removeAssignmentMutation.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(removeAssignmentMutation.error as any)?.response?.data?.error || 'Failed to remove assignment'}
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserClientAssignmentDialog;
